import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

export default function ComingSoon3D() {
  const mountRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showModal, setShowModal] = useState(false);
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (!mountRef.current) return;
    
    const currentMount = mountRef.current; // Store ref value for cleanup
    
    // ==================== SCENE SETUP ====================
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xE8DCC4); // Soft tan background
    scene.fog = new THREE.Fog(0xE8DCC4, 50, 80);
    
    // Camera - Isometric (responsive - zoom out MORE on mobile)
    const aspect = window.innerWidth / window.innerHeight;
    const isMobile = window.innerWidth < 768;
    
    // Mobile needs EVEN BIGGER frustum to see whole office
    const frustumSize = isMobile ? 65 : 18; // Increased from 28 to 40 for mobile
    
    const camera = new THREE.OrthographicCamera(
      -frustumSize * aspect, frustumSize * aspect, frustumSize, -frustumSize, 0.1, 100
    );
    camera.position.set(20, 20, 20);
    camera.lookAt(0, 0, 0);
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    // Performance optimizations
    renderer.sortObjects = false; // Disable sorting for better performance
    renderer.physicallyCorrectLights = false; // Disable for better performance
    currentMount.appendChild(renderer.domElement);
    
    // ==================== LIGHTING ====================
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xfff5e6, 1.2);
    directionalLight.position.set(15, 25, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.left = -25;
    directionalLight.shadow.camera.right = 25;
    directionalLight.shadow.camera.top = 25;
    directionalLight.shadow.camera.bottom = -25;
    // Optimized shadow map size (balanced quality/performance)
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.bias = -0.0001;
    directionalLight.shadow.normalBias = 0.02;
    scene.add(directionalLight);
    
    const fillLight = new THREE.DirectionalLight(0xadd8e6, 0.3);
    fillLight.position.set(-10, 10, -10);
    scene.add(fillLight);
    
    // ==================== FLOOR ====================
    
    // Large ground plane around the office (soft tan to match background)
    const groundGeometry = new THREE.PlaneGeometry(100, 100);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xD4C4B0, // Soft tan/beige
      roughness: 0.95,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    scene.add(ground);
    
    // Office floor base
    const floorGeometry = new THREE.PlaneGeometry(40, 40);
    const floorMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xE5E5E5,
      roughness: 0.9,
      metalness: 0.1,
    });
    const officeFloor = new THREE.Mesh(floorGeometry, floorMaterial);
    officeFloor.rotation.x = -Math.PI / 2;
    officeFloor.receiveShadow = true;
    scene.add(officeFloor);
    
    // Floor tiles - optimized with material and geometry reuse
    const tileSize = 2;
    const lightTileMat = new THREE.MeshStandardMaterial({ 
      color: 0xD8D8D8,
      roughness: 0.95,
    });
    const darkTileMat = new THREE.MeshStandardMaterial({ 
      color: 0xCCCCCC,
      roughness: 0.95,
    });
    const groutMat = new THREE.MeshStandardMaterial({ 
      color: 0xA8A8A8,
      roughness: 0.98,
    });
    
    // Reuse geometries for better performance
    const tileGeo = new THREE.PlaneGeometry(tileSize - 0.05, tileSize - 0.05);
    const groutHGeo = new THREE.PlaneGeometry(tileSize, 0.02);
    const groutVGeo = new THREE.PlaneGeometry(0.02, tileSize);
    
    for (let x = -20; x < 20; x += tileSize) {
      for (let z = -20; z < 20; z += tileSize) {
        const isEven = ((x / tileSize + z / tileSize) % 2 === 0);
        
        // Main tile (reuse geometry)
        const tile = new THREE.Mesh(
          tileGeo,
          isEven ? lightTileMat : darkTileMat
        );
        tile.rotation.x = -Math.PI / 2;
        tile.position.set(x + tileSize / 2, 0.01, z + tileSize / 2);
        tile.castShadow = false; // Tiles don't need to cast shadows
        tile.receiveShadow = true;
        scene.add(tile);
        
        // Horizontal grout line (reuse geometry)
        const groutH = new THREE.Mesh(groutHGeo, groutMat);
        groutH.rotation.x = -Math.PI / 2;
        groutH.position.set(x + tileSize / 2, 0.005, z + tileSize);
        groutH.castShadow = false;
        groutH.receiveShadow = true;
        scene.add(groutH);
        
        // Vertical grout line (reuse geometry)
        const groutV = new THREE.Mesh(groutVGeo, groutMat);
        groutV.rotation.x = -Math.PI / 2;
        groutV.position.set(x + tileSize, 0.005, z + tileSize / 2);
        groutV.castShadow = false;
        groutV.receiveShadow = true;
        scene.add(groutV);
      }
    }
    
    // ==================== WALLS ====================
    const wallMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xF0F0F0,
      roughness: 0.8,
      metalness: 0.05,
    });
    
    // Back wall - split into sections around door opening
    // Door opening is 6.5 units wide centered at x=0 (from x=-3.25 to x=3.25)
    // Total wall width is 40 units (from x=-20 to x=20)
    // Left section: from x=-20 to x=-3.25 = 16.75 units wide
    // Right section: from x=3.25 to x=20 = 16.75 units wide
    
    const backWallLeft = new THREE.Mesh(
      new THREE.BoxGeometry(16.75, 6, 0.3),
      wallMaterial
    );
    backWallLeft.position.set(-11.625, 3, -20);
    backWallLeft.castShadow = true;
    backWallLeft.receiveShadow = true;
    scene.add(backWallLeft);
    
    const backWallRight = new THREE.Mesh(
      new THREE.BoxGeometry(16.75, 6, 0.3),
      wallMaterial
    );
    backWallRight.position.set(11.625, 3, -20);
    backWallRight.castShadow = true;
    backWallRight.receiveShadow = true;
    scene.add(backWallRight);
    
    // Wall section above doors (from door top at ~3.8 to ceiling at 6)
    const backWallAboveDoors = new THREE.Mesh(
      new THREE.BoxGeometry(6.5, 2.2, 0.3),
      wallMaterial
    );
    backWallAboveDoors.position.set(0, 4.9, -20);
    backWallAboveDoors.castShadow = true;
    backWallAboveDoors.receiveShadow = true;
    scene.add(backWallAboveDoors);
    
    // ========== ENTRY DOORS ==========
    const entryDoorsGroup = new THREE.Group();
    
    // Door frame material
    const doorFrameMat = new THREE.MeshStandardMaterial({ 
      color: 0x3a3a3a,
      roughness: 0.4,
      metalness: 0.7,
    });
    
    // Door material (wood)
    const doorMat = new THREE.MeshStandardMaterial({ 
      color: 0x5a4a3a,
      roughness: 0.6,
      metalness: 0.1,
    });
    
    // Glass window material
    const doorGlassMat = new THREE.MeshStandardMaterial({ 
      color: 0xCCE5FF,
      transparent: true,
      opacity: 0.4,
      roughness: 0.1,
    });
    
    // Door frame (surrounding structure)
    const doorFrameTop = new THREE.Mesh(
      new THREE.BoxGeometry(6.5, 0.3, 0.35),
      doorFrameMat
    );
    doorFrameTop.position.set(0, 2.85, 0);
    doorFrameTop.castShadow = true;
    entryDoorsGroup.add(doorFrameTop);
    
    const doorFrameLeft = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 2.8, 0.35),
      doorFrameMat
    );
    doorFrameLeft.position.set(-3.15, 1.4, 0);
    doorFrameLeft.castShadow = true;
    entryDoorsGroup.add(doorFrameLeft);
    
    const doorFrameRight = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 2.8, 0.35),
      doorFrameMat
    );
    doorFrameRight.position.set(3.15, 1.4, 0);
    doorFrameRight.castShadow = true;
    entryDoorsGroup.add(doorFrameRight);
    
    // Center divider between doors
    const centerDivider = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 2.8, 0.35),
      doorFrameMat
    );
    centerDivider.position.set(0, 1.4, 0);
    centerDivider.castShadow = true;
    entryDoorsGroup.add(centerDivider);
    
    // Left door
    const leftDoor = new THREE.Mesh(
      new THREE.BoxGeometry(2.9, 2.6, 0.1),
      doorMat
    );
    leftDoor.position.set(-1.5, 1.3, 0);
    leftDoor.castShadow = true;
    entryDoorsGroup.add(leftDoor);
    
    // Left door window
    const leftDoorWindow = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 1.2, 0.08),
      doorGlassMat
    );
    leftDoorWindow.position.set(-1.5, 1.8, 0.02);
    entryDoorsGroup.add(leftDoorWindow);
    
    // Left door handle
    const leftDoorHandle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.04, 0.25, 16),
      new THREE.MeshStandardMaterial({ color: 0xD4AF37, roughness: 0.2, metalness: 0.9 })
    );
    leftDoorHandle.rotation.z = Math.PI / 2;
    leftDoorHandle.position.set(-0.5, 1.1, 0.08);
    entryDoorsGroup.add(leftDoorHandle);
    
    // Right door
    const rightDoor = new THREE.Mesh(
      new THREE.BoxGeometry(2.9, 2.6, 0.1),
      doorMat
    );
    rightDoor.position.set(1.5, 1.3, 0);
    rightDoor.castShadow = true;
    entryDoorsGroup.add(rightDoor);
    
    // Right door window
    const rightDoorWindow = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 1.2, 0.08),
      doorGlassMat
    );
    rightDoorWindow.position.set(1.5, 1.8, 0.02);
    entryDoorsGroup.add(rightDoorWindow);
    
    // Right door handle
    const rightDoorHandle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.04, 0.25, 16),
      new THREE.MeshStandardMaterial({ color: 0xD4AF37, roughness: 0.2, metalness: 0.9 })
    );
    rightDoorHandle.rotation.z = Math.PI / 2;
    rightDoorHandle.position.set(0.5, 1.1, 0.08);
    entryDoorsGroup.add(rightDoorHandle);
    
    // Door panels (decorative)
    [-1.5, 1.5].forEach(xPos => {
      const panelTop = new THREE.Mesh(
        new THREE.BoxGeometry(2.5, 0.15, 0.05),
        new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.7 })
      );
      panelTop.position.set(xPos, 0.6, 0.06);
      entryDoorsGroup.add(panelTop);
      
      const panelBottom = new THREE.Mesh(
        new THREE.BoxGeometry(2.5, 0.4, 0.05),
        new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.7 })
      );
      panelBottom.position.set(xPos, 0.25, 0.06);
      entryDoorsGroup.add(panelBottom);
    });
    
    // Transom window above doors
    const transomWindow = new THREE.Mesh(
      new THREE.BoxGeometry(6, 0.6, 0.08),
      doorGlassMat
    );
    transomWindow.position.set(0, 3.4, 0);
    entryDoorsGroup.add(transomWindow);
    
    const transomFrame = new THREE.Mesh(
      new THREE.BoxGeometry(6.2, 0.7, 0.12),
      doorFrameMat
    );
    transomFrame.position.set(0, 3.4, -0.02);
    transomFrame.castShadow = true;
    entryDoorsGroup.add(transomFrame);
    
    // Position entry doors at the back wall
    entryDoorsGroup.position.set(0, 0, -19.95);
    scene.add(entryDoorsGroup);
    
    const leftWall = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 6, 40),
      wallMaterial
    );
    leftWall.position.set(-20, 3, 0);
    leftWall.castShadow = true;
    leftWall.receiveShadow = true;
    scene.add(leftWall);
    
    // ========== SERVER TOWERS (Individual units facing office) ==========
    const serverLights = []; // Store lights for animation
    
    // Create individual server towers along the left wall
    const numServers = 16; // Number of server towers
    const serverSpacing = 1.1; // HALF the distance - closer together
    const startZ = -17; // Starting position
    
    for (let i = 0; i < numServers; i++) {
      const serverTower = new THREE.Group();
      
      // Server cabinet body
      const cabinetMat = new THREE.MeshStandardMaterial({ 
        color: 0x1a1a1a,
        roughness: 0.3,
        metalness: 0.8,
      });
      
      const cabinet = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 4.5, 0.8),
        cabinetMat
      );
      cabinet.position.y = 2.25;
      cabinet.castShadow = true;
      cabinet.receiveShadow = true;
      serverTower.add(cabinet);
      
      // Front panel (facing the office)
      const frontPanelMat = new THREE.MeshStandardMaterial({ 
        color: 0x0a0a0a,
        roughness: 0.2,
        metalness: 0.9,
      });
      
      const frontPanel = new THREE.Mesh(
        new THREE.BoxGeometry(0.62, 4.48, 0.05),
        frontPanelMat
      );
      frontPanel.position.set(0, 2.25, 0.4);
      serverTower.add(frontPanel);
      
      // Server unit slots (stacked vertically)
      const slotMat = new THREE.MeshStandardMaterial({ 
        color: 0x2a2a2a,
        roughness: 0.4,
        metalness: 0.7,
      });
      
      for (let slot = 0; slot < 12; slot++) {
        const slotY = 0.5 + slot * 0.35;
        
        // Slot face
        const slotFace = new THREE.Mesh(
          new THREE.BoxGeometry(0.55, 0.3, 0.03),
          slotMat
        );
        slotFace.position.set(0, slotY, 0.43);
        serverTower.add(slotFace);
        
        // Ventilation holes
        for (let v = -0.2; v <= 0.2; v += 0.08) {
          const vent = new THREE.Mesh(
            new THREE.BoxGeometry(0.05, 0.15, 0.02),
            new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.6 })
          );
          vent.position.set(v, slotY, 0.44);
          serverTower.add(vent);
        }
        
        // Status lights for this slot
        const numLights = 3 + Math.floor(Math.random() * 3);
        for (let l = 0; l < numLights; l++) {
          const isGreen = Math.random() > 0.3; // 70% green, 30% red
          const lightColor = isGreen ? 0x00ff00 : 0xff0000;
          const emissiveColor = isGreen ? 0x00aa00 : 0xaa0000;
          
          const light = new THREE.Mesh(
            new THREE.BoxGeometry(0.04, 0.04, 0.02),
            new THREE.MeshStandardMaterial({ 
              color: lightColor,
              emissive: emissiveColor,
              emissiveIntensity: 0.8,
              roughness: 0.1,
              metalness: 0.5,
            })
          );
          light.position.set(
            -0.18 + l * 0.08, 
            slotY + 0.08, 
            0.45
          );
          serverTower.add(light);
          
          // Store light for animation
          serverLights.push({
            mesh: light,
            isGreen: isGreen,
            blinkSpeed: 0.5 + Math.random() * 2.5,
            phase: Math.random() * Math.PI * 2,
          });
        }
        
        // Blue power indicators (always on)
        const powerLight = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.03, 0.02),
          new THREE.MeshStandardMaterial({ 
            color: 0x0088ff,
            emissive: 0x0066cc,
            emissiveIntensity: 0.6,
            roughness: 0.1,
          })
        );
        powerLight.position.set(0.22, slotY - 0.08, 0.45);
        serverTower.add(powerLight);
      }
      
      // Top ventilation grill
      const topGrill = new THREE.Mesh(
        new THREE.BoxGeometry(0.58, 0.05, 0.78),
        new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5 })
      );
      topGrill.position.set(0, 4.52, 0);
      serverTower.add(topGrill);
      
      // Bottom base
      const base = new THREE.Mesh(
        new THREE.BoxGeometry(0.62, 0.1, 0.82),
        new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.6, metalness: 0.7 })
      );
      base.position.set(0, 0.05, 0);
      base.castShadow = true;
      serverTower.add(base);
      
      // Rubber feet
      [-0.25, 0.25].forEach(x => {
        [-0.35, 0.35].forEach(z => {
          const foot = new THREE.Mesh(
            new THREE.CylinderGeometry(0.03, 0.035, 0.04, 12),
            new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.9 })
          );
          foot.position.set(x, 0.02, z);
          serverTower.add(foot);
        });
      });
      
      // Position each tower along the wall
      serverTower.position.set(-19.5, 0, startZ + i * serverSpacing);
      serverTower.rotation.y = Math.PI / 2; // Face the office
      scene.add(serverTower);
    }
    const baseboardMaterial = new THREE.MeshStandardMaterial({ color: 0xC0C0C0 });
    const baseboard1 = new THREE.Mesh(
      new THREE.BoxGeometry(40, 0.2, 0.1),
      baseboardMaterial
    );
    baseboard1.position.set(0, 0.1, -19.85);
    scene.add(baseboard1);
    
    const baseboard2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.2, 40),
      baseboardMaterial
    );
    baseboard2.position.set(-19.85, 0.1, 0);
    scene.add(baseboard2);
    
    // ==================== OFFICE FURNITURE ====================
    
    // Enhanced cubicles
    const cubicleMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xE8E8E8,
      roughness: 0.7,
      metalness: 0.05,
    });
    
    const cubiclePositions = [
      { x: -8, z: -8 },
      { x: -8, z: -2 },
      { x: -8, z: 4 },
      { x: -2, z: -8 },
      { x: -2, z: 4 },
      { x: 4, z: -8 },
      { x: 4, z: -2 },
      { x: 4, z: 4 },
    ];
    
    cubiclePositions.forEach(pos => {
      const wallHeight = 1.8;
      const wallThickness = 0.08;
      
      // Back wall
      const wall1 = new THREE.Mesh(
        new THREE.BoxGeometry(3, wallHeight, wallThickness),
        cubicleMaterial
      );
      wall1.position.set(pos.x, wallHeight / 2, pos.z - 1.5);
      wall1.castShadow = true;
      wall1.receiveShadow = true;
      scene.add(wall1);
      
      // Left wall
      const wall2 = new THREE.Mesh(
        new THREE.BoxGeometry(wallThickness, wallHeight, 3),
        cubicleMaterial
      );
      wall2.position.set(pos.x - 1.5, wallHeight / 2, pos.z);
      wall2.castShadow = true;
      wall2.receiveShadow = true;
      scene.add(wall2);
      
      // Wall trim
      const trimMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xB0B0B0,
        roughness: 0.5,
      });
      const trimTop1 = new THREE.Mesh(
        new THREE.BoxGeometry(3, 0.05, 0.1),
        trimMaterial
      );
      trimTop1.position.set(pos.x, wallHeight, pos.z - 1.5);
      scene.add(trimTop1);
      
      // Desk
      const deskTopGeometry = new THREE.BoxGeometry(2.2, 0.08, 1.4);
      const deskMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xA0826D,
        roughness: 0.6,
        metalness: 0.1,
      });
      const deskTop = new THREE.Mesh(deskTopGeometry, deskMaterial);
      deskTop.position.set(pos.x, 0.75, pos.z - 0.5);
      deskTop.castShadow = true;
      deskTop.receiveShadow = true;
      scene.add(deskTop);
      
      // Desk edge
      const edgeTrim = new THREE.Mesh(
        new THREE.BoxGeometry(2.25, 0.05, 1.45),
        new THREE.MeshStandardMaterial({ color: 0x8B6F5C, roughness: 0.5 })
      );
      edgeTrim.position.set(pos.x, 0.78, pos.z - 0.5);
      scene.add(edgeTrim);
      
      // Desk legs
      const legGeom = new THREE.CylinderGeometry(0.04, 0.04, 0.7, 16);
      const legMat = new THREE.MeshStandardMaterial({ 
        color: 0x505050,
        roughness: 0.4,
        metalness: 0.7,
      });
      
      [-1, 1].forEach(x => {
        [-0.6, 0.4].forEach(z => {
          const leg = new THREE.Mesh(legGeom, legMat);
          leg.position.set(pos.x + x, 0.35, pos.z - 0.5 + z);
          leg.castShadow = true;
          scene.add(leg);
        });
      });
      
      // Monitor
      const monitorStand = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.12, 0.15, 16),
        new THREE.MeshStandardMaterial({ color: 0x2C2C2C, roughness: 0.4, metalness: 0.6 })
      );
      monitorStand.position.set(pos.x, 0.87, pos.z - 0.5);
      scene.add(monitorStand);
      
      const monitorGeometry = new THREE.BoxGeometry(0.8, 0.55, 0.06);
      const monitorMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x1a1a1a,
        roughness: 0.3,
        metalness: 0.5,
      });
      const monitor = new THREE.Mesh(monitorGeometry, monitorMaterial);
      monitor.position.set(pos.x, 1.2, pos.z - 0.5);
      monitor.rotation.x = -0.1;
      monitor.castShadow = true;
      scene.add(monitor);
      
      // Screen
      const screenGeometry = new THREE.PlaneGeometry(0.72, 0.45);
      const screenMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x2A4A6A,
        emissive: 0x1a2a3a,
        emissiveIntensity: 0.5,
        roughness: 0.2,
      });
      const screen = new THREE.Mesh(screenGeometry, screenMaterial);
      screen.position.set(pos.x, 1.2, pos.z - 0.47);
      screen.rotation.x = -0.1;
      scene.add(screen);
      
      // Keyboard
      const keyboard = new THREE.Mesh(
        new THREE.BoxGeometry(0.45, 0.02, 0.15),
        new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.7 })
      );
      keyboard.position.set(pos.x, 0.8, pos.z + 0.1);
      keyboard.castShadow = true;
      scene.add(keyboard);
      
      // Mouse
      const mouse = new THREE.Mesh(
        new THREE.BoxGeometry(0.07, 0.03, 0.1),
        new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5 })
      );
      mouse.position.set(pos.x + 0.3, 0.8, pos.z + 0.1);
      mouse.castShadow = true;
      scene.add(mouse);
      
      // Office chair
      const chairSeat = new THREE.Mesh(
        new THREE.CylinderGeometry(0.35, 0.35, 0.08, 32),
        new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.6 })
      );
      chairSeat.position.set(pos.x, 0.5, pos.z + 0.8);
      chairSeat.castShadow = true;
      scene.add(chairSeat);
      
      const chairBack = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 0.7, 0.08),
        new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.6 })
      );
      chairBack.position.set(pos.x, 0.8, pos.z + 1.1);
      chairBack.castShadow = true;
      scene.add(chairBack);
      
      const chairPole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.03, 0.4, 16),
        new THREE.MeshStandardMaterial({ color: 0x505050, roughness: 0.3, metalness: 0.7 })
      );
      chairPole.position.set(pos.x, 0.25, pos.z + 0.8);
      scene.add(chairPole);
    });
    
    // ========== FILLING EMPTY CORNERS ==========
    
    // Bottom-left corner - Enhanced Break Room Area
    const breakAreaGroup = new THREE.Group();
    
    // Large L-shaped couch
    const couchMat = new THREE.MeshStandardMaterial({ color: 0x4a5a6a, roughness: 0.8 });
    const couchDarkMat = new THREE.MeshStandardMaterial({ color: 0x3a4a5a, roughness: 0.8 });
    
    // Main couch section
    const couchBase1 = new THREE.Mesh(
      new THREE.BoxGeometry(3.5, 0.4, 1.2),
      couchMat
    );
    couchBase1.position.set(0, 0.4, 0);
    couchBase1.castShadow = true;
    breakAreaGroup.add(couchBase1);
    
    const couchBack1 = new THREE.Mesh(
      new THREE.BoxGeometry(3.5, 0.8, 0.2),
      couchMat
    );
    couchBack1.position.set(0, 0.8, -0.5);
    couchBack1.castShadow = true;
    breakAreaGroup.add(couchBack1);
    
    // L-section of couch (adjusted to not overlap with main couch)
    const couchBase2 = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.4, 2.2),
      couchMat
    );
    couchBase2.position.set(-2.15, 0.4, 1.7); // Moved forward (z increased) to avoid overlap
    couchBase2.castShadow = true;
    breakAreaGroup.add(couchBase2);
    
    const couchBack2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 0.8, 2.2),
      couchMat
    );
    couchBack2.position.set(-2.75, 0.8, 1.7); // Moved forward to match base
    couchBack2.castShadow = true;
    breakAreaGroup.add(couchBack2);
    
    // Couch cushions (for detail)
    const cushionMat = new THREE.MeshStandardMaterial({ color: 0x5a6a7a, roughness: 0.85 });
    
    for (let i = 0; i < 3; i++) {
      const cushion = new THREE.Mesh(
        new THREE.BoxGeometry(1, 0.15, 0.9),
        cushionMat
      );
      cushion.position.set(-1.2 + i * 1.2, 0.55, 0.1);
      cushion.castShadow = true;
      breakAreaGroup.add(cushion);
    }
    
    // Couch arms
    [-1.65, 1.65].forEach(x => {
      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.2, 0.6, 1.2),
        couchDarkMat
      );
      arm.position.set(x, 0.6, 0);
      arm.castShadow = true;
      breakAreaGroup.add(arm);
    });
    
    // Coffee table (larger)
    const coffeeTableTop = new THREE.Mesh(
      new THREE.BoxGeometry(2, 0.08, 1.2),
      new THREE.MeshStandardMaterial({ color: 0xA0826D, roughness: 0.5 })
    );
    coffeeTableTop.position.set(0.5, 0.4, 1.8);
    coffeeTableTop.castShadow = true;
    coffeeTableTop.receiveShadow = true;
    breakAreaGroup.add(coffeeTableTop);
    
    // Table edge trim
    const coffeeTableEdge = new THREE.Mesh(
      new THREE.BoxGeometry(2.05, 0.05, 1.25),
      new THREE.MeshStandardMaterial({ color: 0x8B6F5C, roughness: 0.4 })
    );
    coffeeTableEdge.position.set(0.5, 0.42, 1.8);
    breakAreaGroup.add(coffeeTableEdge);
    
    // Coffee table legs
    [-0.8, 0.8].forEach(x => {
      [-0.45, 0.45].forEach(z => {
        const leg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.04, 0.04, 0.38, 12),
          new THREE.MeshStandardMaterial({ color: 0x505050, metalness: 0.6 })
        );
        leg.position.set(0.5 + x, 0.19, 1.8 + z);
        leg.castShadow = true;
        breakAreaGroup.add(leg);
      });
    });
    
    // Stack of magazines on coffee table
    const magazineStack = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.08, 0.4),
      new THREE.MeshStandardMaterial({ color: 0xE8E8E8, roughness: 0.7 })
    );
    magazineStack.position.set(0.3, 0.48, 1.6);
    magazineStack.castShadow = true;
    breakAreaGroup.add(magazineStack);
    
    // Coffee mug on table
    const mugBody = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.12, 16),
      new THREE.MeshStandardMaterial({ color: 0x8B0000, roughness: 0.6 })
    );
    mugBody.position.set(0.8, 0.5, 1.9);
    mugBody.castShadow = true;
    breakAreaGroup.add(mugBody);
    
    const mugHandle = new THREE.Mesh(
      new THREE.TorusGeometry(0.06, 0.015, 8, 16, Math.PI),
      new THREE.MeshStandardMaterial({ color: 0x8B0000, roughness: 0.6 })
    );
    mugHandle.rotation.z = -Math.PI / 2;
    mugHandle.position.set(0.88, 0.5, 1.9);
    breakAreaGroup.add(mugHandle);
    
    // Additional chairs around the area
    const chairMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.6 });
    
    // Chair 1
    const chair1 = new THREE.Group();
    const chair1Seat = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.08, 0.5),
      chairMat
    );
    chair1Seat.position.y = 0.45;
    chair1Seat.castShadow = true;
    chair1.add(chair1Seat);
    
    const chair1Back = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.6, 0.08),
      chairMat
    );
    chair1Back.position.set(0, 0.7, -0.21);
    chair1Back.castShadow = true;
    chair1.add(chair1Back);
    
    // Chair legs
    [-0.2, 0.2].forEach(x => {
      [-0.2, 0.2].forEach(z => {
        const leg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.025, 0.025, 0.45, 12),
          new THREE.MeshStandardMaterial({ color: 0x2a2a2a, metalness: 0.5 })
        );
        leg.position.set(x, 0.225, z);
        leg.castShadow = true;
        chair1.add(leg);
      });
    });
    
    chair1.position.set(2, 0, 1.5);
    chair1.rotation.y = -Math.PI / 4;
    breakAreaGroup.add(chair1);
    
    // Chair 2 (clone and reposition)
    const chair2 = chair1.clone();
    chair2.position.set(-1, 0, 2.8);
    chair2.rotation.y = Math.PI / 6;
    breakAreaGroup.add(chair2);
    
    // Vending machine
    const vendingMachine = new THREE.Group();
    
    const vendingBody = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1.8, 0.6),
      new THREE.MeshStandardMaterial({ color: 0xC41E3A, roughness: 0.4, metalness: 0.3 })
    );
    vendingBody.position.y = 0.9;
    vendingBody.castShadow = true;
    vendingMachine.add(vendingBody);
    
    // Vending window
    const vendingWindow = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 1.2, 0.05),
      new THREE.MeshStandardMaterial({ 
        color: 0x88CCFF,
        transparent: true,
        opacity: 0.5,
        roughness: 0.1,
      })
    );
    vendingWindow.position.set(0, 1, 0.28);
    vendingMachine.add(vendingWindow);
    
    // Vending machine logo panel
    const logoPanel = new THREE.Mesh(
      new THREE.BoxGeometry(0.6, 0.2, 0.05),
      new THREE.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 0.5 })
    );
    logoPanel.position.set(0, 1.7, 0.28);
    vendingMachine.add(logoPanel);
    
    // Coin slot
    const coinSlot = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 0.08, 0.05),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, metalness: 0.8 })
    );
    coinSlot.position.set(0.3, 0.5, 0.28);
    vendingMachine.add(coinSlot);
    
    vendingMachine.position.set(2.5, 0, -0.8);
    breakAreaGroup.add(vendingMachine);

    // Water cooler
    const waterCoolerGroup = new THREE.Group();

    const coolerBase = new THREE.Mesh(
      new THREE.CylinderGeometry(0.35, 0.4, 0.5, 32),
      new THREE.MeshStandardMaterial({ 
        color: 0x3a3a3a,
        roughness: 0.5,
        metalness: 0.3,
      })
    );
    coolerBase.position.y = 0.25;
    coolerBase.castShadow = true;
    waterCoolerGroup.add(coolerBase);
    
    const waterContainer = new THREE.Mesh(
      new THREE.CylinderGeometry(0.28, 0.28, 1.4, 32),
      new THREE.MeshStandardMaterial({ 
        color: 0x6BB6FF,
        transparent: true,
        opacity: 0.4,
        roughness: 0.1,
        metalness: 0.1,
      })
    );
    waterContainer.position.y = 1.2;
    waterContainer.castShadow = true;
    waterCoolerGroup.add(waterContainer);
    
    const water = new THREE.Mesh(
      new THREE.CylinderGeometry(0.26, 0.26, 1.0, 32),
      new THREE.MeshStandardMaterial({ 
        color: 0x4A9FE8,
        transparent: true,
        opacity: 0.6,
        roughness: 0.1,
      })
    );
    water.position.y = 0.95;
    waterCoolerGroup.add(water);
    
    const dispenser = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 0.25, 0.12),
      new THREE.MeshStandardMaterial({ 
        color: 0x2a2a2a,
        roughness: 0.4,
        metalness: 0.5,
      })
    );
    dispenser.position.set(0, 0.6, 0.3);
    dispenser.castShadow = true;
    waterCoolerGroup.add(dispenser);
    
    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 0.28, 0.15, 32),
      new THREE.MeshStandardMaterial({ 
        color: 0x4A9FE8,
        transparent: true,
        opacity: 0.5,
        roughness: 0.2,
      })
    );
    cap.position.y = 1.95;
    cap.castShadow = true;
    waterCoolerGroup.add(cap);
    
    waterCoolerGroup.position.set(-9, 0, 13);
    scene.add(waterCoolerGroup);
    
    // Microwave on counter
    const counter = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.1, 0.6),
      new THREE.MeshStandardMaterial({ color: 0xD0D0D0, roughness: 0.5 })
    );
    counter.position.set(-2, 0.9, -1);
    counter.castShadow = true;
    counter.receiveShadow = true;
    breakAreaGroup.add(counter);
    
    // Counter legs
    [-0.65, 0.65].forEach(x => {
      const counterLeg = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.85, 0.08),
        new THREE.MeshStandardMaterial({ color: 0xA0A0A0, metalness: 0.5 })
      );
      counterLeg.position.set(-2 + x, 0.425, -1);
      counterLeg.castShadow = true;
      breakAreaGroup.add(counterLeg);
    });
    
    const microwave = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.35, 0.4),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5, metalness: 0.4 })
    );
    microwave.position.set(-2, 1.13, -1);
    microwave.castShadow = true;
    breakAreaGroup.add(microwave);
    
    // Microwave window
    const microwaveWindow = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.25, 0.02),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.2 })
    );
    microwaveWindow.position.set(-2, 1.13, -0.79);
    breakAreaGroup.add(microwaveWindow);
    
    // Coffee maker
    const coffeeMaker = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.4, 0.25),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.6 })
    );
    coffeeMaker.position.set(-1.5, 1.15, -1);
    coffeeMaker.castShadow = true;
    breakAreaGroup.add(coffeeMaker);
    
    // Coffee pot (glass)
    const coffeePot = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.1, 0.2, 16),
      new THREE.MeshStandardMaterial({ 
        color: 0x8B4513,
        transparent: true,
        opacity: 0.6,
        roughness: 0.2,
      })
    );
    coffeePot.position.set(-1.5, 1.05, -0.85);
    coffeePot.castShadow = true;
    breakAreaGroup.add(coffeePot);
    
    // Dividing wall for clock (separates break area from main office)
    // Extended to reach further into the office space
    const dividingWall = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 3, 12),
      wallMaterial
    );
    dividingWall.position.set(0, 1.5, -2);
    dividingWall.rotation.y = Math.PI / 2; // Rotate 90 degrees
    dividingWall.castShadow = true;
    dividingWall.receiveShadow = true;
    breakAreaGroup.add(dividingWall);
    
    // Wall clock (mounted on dividing wall)
    const clockBack = new THREE.Mesh(
      new THREE.CylinderGeometry(0.25, 0.25, 0.05, 32),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5 })
    );
    clockBack.rotation.x = Math.PI / 2;
    clockBack.position.set(0.11, 2.2, -1.81); // Mounted on wall (x adjusted for rotated wall)
    clockBack.castShadow = true;
    breakAreaGroup.add(clockBack);
    
    const clockFace = new THREE.Mesh(
      new THREE.CylinderGeometry(0.23, 0.23, 0.02, 32),
      new THREE.MeshStandardMaterial({ color: 0xF5F5F5, roughness: 0.6 })
    );
    clockFace.rotation.x = Math.PI / 2;
    clockFace.position.set(0.13, 2.2, -1.75); // Slightly forward of clock back
    breakAreaGroup.add(clockFace);
    
    // Small trash bin
    const trashBin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.18, 0.4, 16),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.7 })
    );
    trashBin.position.set(2.8, 0.2, 2.5);
    trashBin.castShadow = true;
    breakAreaGroup.add(trashBin);
    
    breakAreaGroup.position.set(-14, 0, 14);
    scene.add(breakAreaGroup);
    
    // ========== PIZZA TRASH AREA (Bottom right, below printer) ==========
    const pizzaTrashGroup = new THREE.Group();
    
    // Trash can 1 - overflowing (BIGGER)
    const trashCan1 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.45, 1, 16),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.7 })
    );
    trashCan1.position.set(-0.8, 0.5, 0);
    trashCan1.castShadow = true;
    pizzaTrashGroup.add(trashCan1);
    
    // Trash can 2 - overflowing (BIGGER)
    const trashCan2 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.45, 1, 16),
      new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.7 })
    );
    trashCan2.position.set(0.8, 0.5, 0);
    trashCan2.castShadow = true;
    pizzaTrashGroup.add(trashCan2);
    
    // Pizza slice material
    const pizzaMat = new THREE.MeshStandardMaterial({ 
      color: 0xFDB813, // Bright cheese yellow
      roughness: 0.8 
    });
    
    const crustMat = new THREE.MeshStandardMaterial({ 
      color: 0xC17817, // Dark crust brown
      roughness: 0.85 
    });
    
    const toppingMat = new THREE.MeshStandardMaterial({ 
      color: 0xC41E3A, // Bright pepperoni red
      roughness: 0.7 
    });
    
    // Create BIGGER pizza slices overflowing from trash cans - MORE SLICES
    const pizzaSlicePositions = [
      // Trash can 1 overflow
      { x: -0.8, y: 1.1, z: 0, rot: 0.3 },
      { x: -1, y: 1.15, z: 0.2, rot: -0.5 },
      { x: -0.6, y: 1.13, z: -0.2, rot: 0.8 },
      { x: -0.9, y: 1.2, z: 0.1, rot: 1.2 },
      { x: -0.85, y: 1.18, z: -0.15, rot: 0.6 },
      { x: -0.7, y: 1.16, z: 0.15, rot: -0.8 },
      // Trash can 2 overflow
      { x: 0.8, y: 1.08, z: 0, rot: -0.4 },
      { x: 1, y: 1.12, z: 0.2, rot: 0.6 },
      { x: 0.6, y: 1.18, z: -0.2, rot: -0.9 },
      { x: 0.9, y: 1.22, z: 0.15, rot: 1.5 },
      { x: 0.85, y: 1.14, z: -0.15, rot: -0.3 },
      { x: 0.7, y: 1.2, z: 0.1, rot: 0.9 },
      // On ground around trash cans - MORE SLICES
      { x: -1.3, y: 0.08, z: 0.5, rot: 0.2 },
      { x: 1.2, y: 0.08, z: -0.5, rot: -0.7 },
      { x: 0, y: 0.08, z: 0.7, rot: 1.1 },
      { x: -0.5, y: 0.08, z: -0.7, rot: -1.3 },
      { x: -1.5, y: 0.08, z: -0.3, rot: 0.5 },
      { x: 1.4, y: 0.08, z: 0.3, rot: -1.1 },
      { x: 0.3, y: 0.08, z: 0.9, rot: 0.8 },
      { x: -0.8, y: 0.08, z: 0.8, rot: -0.4 },
      { x: 1, y: 0.08, z: -0.8, rot: 1.3 },
      { x: -0.2, y: 0.08, z: -0.9, rot: -0.6 },
      { x: 0.5, y: 0.08, z: -0.2, rot: 0.4 },
      { x: -1, y: 0.08, z: 0.2, rot: -1.4 },
    ];
    
    pizzaSlicePositions.forEach(pos => {
      const slice = new THREE.Group();
      
      // Pizza triangle (slice) - MUCH BIGGER
      const sliceShape = new THREE.Mesh(
        new THREE.ConeGeometry(0.3, 0.05, 3),
        pizzaMat
      );
      sliceShape.rotation.x = Math.PI / 2;
      sliceShape.castShadow = true;
      slice.add(sliceShape);
      
      // Crust edge - BIGGER
      const crust = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, 0.3, 8),
        crustMat
      );
      crust.rotation.z = Math.PI / 2;
      crust.position.set(0, 0, -0.15);
      crust.castShadow = true;
      slice.add(crust);
      
      // Pepperoni toppings - BIGGER (3-4 per slice)
      for (let i = 0; i < 4; i++) {
        const pepperoni = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05, 0.05, 0.02, 12),
          toppingMat
        );
        pepperoni.rotation.x = Math.PI / 2;
        pepperoni.position.set(
          (Math.random() - 0.5) * 0.15,
          0.02,
          (Math.random() - 0.5) * 0.15 - 0.05
        );
        pepperoni.castShadow = true;
        slice.add(pepperoni);
      }
      
      slice.position.set(pos.x, pos.y, pos.z);
      slice.rotation.y = pos.rot;
      slice.rotation.x = (Math.random() - 0.5) * 0.3;
      pizzaTrashGroup.add(slice);
    });
    
    // Pizza boxes - MUCH BIGGER messy stack of 20 boxes (was 12)
    const boxMat = new THREE.MeshStandardMaterial({ 
      color: 0xE8D4B0, // Light cardboard color
      roughness: 0.9 
    });
    
    const boxLabelMat = new THREE.MeshStandardMaterial({ 
      color: 0xC41E3A, // Red label
      roughness: 0.7 
    });
    
    const boxTextMat = new THREE.MeshStandardMaterial({ 
      color: 0x2a2a2a, // Black text
      roughness: 0.8 
    });
    
    // Stack boxes messily next to trash cans - MORE BOXES
    for (let i = 0; i < 20; i++) {
      const box = new THREE.Group();
      
      // Box body - MUCH BIGGER
      const boxBody = new THREE.Mesh(
        new THREE.BoxGeometry(0.7, 0.08, 0.7),
        boxMat
      );
      boxBody.castShadow = true;
      box.add(boxBody);
      
      // Box lid lines (to show it's closed)
      const lidLine1 = new THREE.Mesh(
        new THREE.BoxGeometry(0.72, 0.01, 0.02),
        boxTextMat
      );
      lidLine1.position.set(0, 0.045, 0);
      box.add(lidLine1);
      
      const lidLine2 = new THREE.Mesh(
        new THREE.BoxGeometry(0.02, 0.01, 0.72),
        boxTextMat
      );
      lidLine2.position.set(0, 0.045, 0);
      box.add(lidLine2);
      
      // Red circle logo on top - BIGGER
      const logo = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15, 0.15, 0.01, 16),
        boxLabelMat
      );
      logo.rotation.x = Math.PI / 2;
      logo.position.set(0, 0.05, 0);
      logo.castShadow = true;
      box.add(logo);
      
      // "PIZZA" text indicator (simple box representation)
      const textBox = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.01, 0.08),
        boxTextMat
      );
      textBox.position.set(0, 0.05, 0.25);
      box.add(textBox);
      
      // Position boxes in messy stack - spread across more area
      const stackLevel = Math.floor(i / 4); // 4 boxes per level (ish)
      const offsetInLevel = i % 4;
      
      box.position.set(
        -2.2 + offsetInLevel * 0.4 + (Math.random() - 0.5) * 0.25,
        0.04 + stackLevel * 0.09 + (Math.random() - 0.5) * 0.03,
        0 + (Math.random() - 0.5) * 0.4
      );
      
      // Messy rotations
      box.rotation.y = (Math.random() - 0.5) * 0.5;
      box.rotation.z = (Math.random() - 0.5) * 0.2;
      box.rotation.x = (Math.random() - 0.5) * 0.15;
      
      pizzaTrashGroup.add(box);
    }
    
    // Position pizza trash area bottom right, below the printer
    pizzaTrashGroup.position.set(14, 0, 18);
    scene.add(pizzaTrashGroup);
    
    // Bottom-right corner - Printer station
    const printerAreaGroup = new THREE.Group();
    
    // Printer table
    const printerTable = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.08, 1.2),
      new THREE.MeshStandardMaterial({ color: 0x808080, roughness: 0.6, metalness: 0.4 })
    );
    printerTable.position.y = 0.75;
    printerTable.castShadow = true;
    printerAreaGroup.add(printerTable);
    
    // Table legs
    [-0.6, 0.6].forEach(x => {
      [-0.5, 0.5].forEach(z => {
        const leg = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.7, 0.08),
          new THREE.MeshStandardMaterial({ color: 0x606060, metalness: 0.5 })
        );
        leg.position.set(x, 0.35, z);
        leg.castShadow = true;
        printerAreaGroup.add(leg);
      });
    });
    
    // Printer
    const printer = new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 0.4, 0.6),
      new THREE.MeshStandardMaterial({ color: 0xE8E8E8, roughness: 0.4 })
    );
    printer.position.set(0, 1.0, 0);
    printer.castShadow = true;
    printerAreaGroup.add(printer);
    
    // Printer paper tray
    const tray = new THREE.Mesh(
      new THREE.BoxGeometry(0.75, 0.05, 0.55),
      new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5 })
    );
    tray.position.set(0, 0.82, 0.15);
    printerAreaGroup.add(tray);
    
    // Paper stack on table
    const paperStack = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.12, 0.45),
      new THREE.MeshStandardMaterial({ color: 0xF5F5F5, roughness: 0.7 })
    );
    paperStack.position.set(0.5, 0.85, -0.3);
    paperStack.castShadow = true;
    printerAreaGroup.add(paperStack);
    
    printerAreaGroup.position.set(14, 0, 14);
    scene.add(printerAreaGroup);
    
    
    // Top-right corner - Luxury Board Room
    const luxuryBoardRoom = new THREE.Group();
    
    // Premium glass walls material
    const premiumGlassMat = new THREE.MeshStandardMaterial({ 
      color: 0xE8F4F8,
      transparent: true,
      opacity: 0.25,
      roughness: 0.05,
      metalness: 0.3,
      side: THREE.DoubleSide,
    });
    
    // Luxury frame material (brushed chrome/gold)
    const luxuryFrameMat = new THREE.MeshStandardMaterial({ 
      color: 0xD4AF37, // Gold
      roughness: 0.2,
      metalness: 0.9,
    });
    
    // Back glass wall - Extended but not too far
    const backGlass = new THREE.Mesh(
      new THREE.BoxGeometry(9, 3, 0.08),
      premiumGlassMat
    );
    backGlass.position.set(1.5, 1.5, -3);
    backGlass.castShadow = true;
    backGlass.receiveShadow = true;
    luxuryBoardRoom.add(backGlass);
    
    // Luxury frame
    const backFrame = new THREE.Mesh(
      new THREE.BoxGeometry(9.1, 3.1, 0.12),
      luxuryFrameMat
    );
    backFrame.position.set(1.5, 1.5, -3.05);
    backFrame.castShadow = true;
    luxuryBoardRoom.add(backFrame);
    
    // Left glass wall - KEEP ORIGINAL SIZE
    const leftGlass = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 3, 5.5),
      premiumGlassMat
    );
    leftGlass.position.set(-3, 1.5, -0.15);
    leftGlass.castShadow = true;
    luxuryBoardRoom.add(leftGlass);
    
    const leftFrame = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 3.1, 5.6),
      luxuryFrameMat
    );
    leftFrame.position.set(-3.05, 1.5, -0.18);
    luxuryBoardRoom.add(leftFrame);
    
    // Front glass wall with door
    const frontLeftGlass = new THREE.Mesh(
      new THREE.BoxGeometry(2, 3, 0.08),
      premiumGlassMat
    );
    frontLeftGlass.position.set(-2, 1.5, 2.5);
    luxuryBoardRoom.add(frontLeftGlass);
    
    const frontRightGlass = new THREE.Mesh(
      new THREE.BoxGeometry(5, 3, 0.08),
      premiumGlassMat
    );
    frontRightGlass.position.set(3.5, 1.5, 2.5);
    luxuryBoardRoom.add(frontRightGlass);
    
    // Glass door
    const glassDoor = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, 2.8, 0.08),
      premiumGlassMat
    );
    glassDoor.position.set(0, 1.4, 2.5);
    luxuryBoardRoom.add(glassDoor);
    
    const doorFrame = new THREE.Mesh(
      new THREE.BoxGeometry(1.9, 2.9, 0.12),
      luxuryFrameMat
    );
    doorFrame.position.set(0, 1.4, 2.55);
    luxuryBoardRoom.add(doorFrame);
    
    // Luxury door handle
    const handleMat = new THREE.MeshStandardMaterial({ 
      color: 0xD4AF37,
      roughness: 0.1,
      metalness: 0.95,
    });
    const doorHandle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.02, 0.02, 0.15, 16),
      handleMat
    );
    doorHandle.rotation.z = Math.PI / 2;
    doorHandle.position.set(0.7, 1.4, 2.6);
    luxuryBoardRoom.add(doorHandle);
    
    // Luxury conference table - Mahogany
    const mahoganyMat = new THREE.MeshStandardMaterial({ 
      color: 0x4A1F1F,
      roughness: 0.3,
      metalness: 0.1,
    });
    
    const tableTop = new THREE.Mesh(
      new THREE.BoxGeometry(4.5, 0.15, 2.5),
      mahoganyMat
    );
    tableTop.position.y = 0.8;
    tableTop.castShadow = true;
    tableTop.receiveShadow = true;
    luxuryBoardRoom.add(tableTop);
    
    // Table edge trim (gold)
    const tableEdge = new THREE.Mesh(
      new THREE.BoxGeometry(4.55, 0.1, 2.55),
      luxuryFrameMat
    );
    tableEdge.position.y = 0.88;
    luxuryBoardRoom.add(tableEdge);
    
    // Table legs - elegant tapered
    const legMat = new THREE.MeshStandardMaterial({ 
      color: 0x2A1A1A,
      roughness: 0.4,
      metalness: 0.6,
    });
    
    [-1.8, 1.8].forEach(x => {
      [-1, 1].forEach(z => {
        const leg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08, 0.12, 0.7, 16),
          legMat
        );
        leg.position.set(x, 0.35, z);
        leg.castShadow = true;
        luxuryBoardRoom.add(leg);
      });
    });
    
    // Luxury executive chairs - leather
    const leatherMat = new THREE.MeshStandardMaterial({ 
      color: 0x2A1A0A, // Rich brown leather
      roughness: 0.4,
      metalness: 0.1,
    });
    
    const chairPositions = [
      { x: -3.5, z: 0, rot: Math.PI / 2 },
      { x: 2.8, z: 0, rot: -Math.PI / 2 }, // Moved inward from 3.5 to 2.8
      { x: -1.5, z: -1.5, rot: 0 },
      { x: 0, z: -1.5, rot: 0 },
      { x: 1.5, z: -1.5, rot: 0 },
      { x: -1.5, z: 1.5, rot: Math.PI },
      { x: 0, z: 1.5, rot: Math.PI },
      { x: 1.5, z: 1.5, rot: Math.PI },
    ];
    
    chairPositions.forEach(pos => {
      const chair = new THREE.Group();
      
      // Seat
      const seat = new THREE.Mesh(
        new THREE.CylinderGeometry(0.32, 0.32, 0.1, 32),
        leatherMat
      );
      seat.position.y = 0.5;
      seat.castShadow = true;
      chair.add(seat);
      
      // Backrest
      const back = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 0.7, 0.1),
        leatherMat
      );
      back.position.set(0, 0.85, -0.2);
      back.castShadow = true;
      chair.add(back);
      
      // Armrests
      const armMat = new THREE.MeshStandardMaterial({ 
        color: 0xD4AF37,
        roughness: 0.2,
        metalness: 0.9,
      });
      
      const leftArm = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.25, 0.35),
        armMat
      );
      leftArm.position.set(-0.28, 0.6, -0.05);
      chair.add(leftArm);
      
      const rightArm = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.25, 0.35),
        armMat
      );
      rightArm.position.set(0.28, 0.6, -0.05);
      chair.add(rightArm);
      
      // Base
      const base = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.06, 0.4, 16),
        legMat
      );
      base.position.y = 0.2;
      base.castShadow = true;
      chair.add(base);
      
      // Wheels
      const wheelMat = new THREE.MeshStandardMaterial({ 
        color: 0x1a1a1a,
        roughness: 0.3,
        metalness: 0.7,
      });
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 3) {
        const wheel = new THREE.Mesh(
          new THREE.SphereGeometry(0.03, 8, 8),
          wheelMat
        );
        wheel.position.set(
          Math.cos(angle) * 0.15,
          0.02,
          Math.sin(angle) * 0.15
        );
        chair.add(wheel);
      }
      
      chair.position.set(pos.x, 0, pos.z);
      chair.rotation.y = pos.rot;
      luxuryBoardRoom.add(chair);
    });
    
    // Projector screen on back wall
    const screenMat = new THREE.MeshStandardMaterial({ 
      color: 0xF5F5F5,
      roughness: 0.6,
      metalness: 0.0,
      emissive: 0xE0E0E0,
      emissiveIntensity: 0.1,
    });
    const screen = new THREE.Mesh(
      new THREE.BoxGeometry(2.5, 1.5, 0.05),
      screenMat
    );
    screen.position.set(0, 2.2, -2.95);
    luxuryBoardRoom.add(screen);
    
    // Screen frame
    const screenFrame = new THREE.Mesh(
      new THREE.BoxGeometry(2.6, 1.6, 0.08),
      luxuryFrameMat
    );
    screenFrame.position.set(0, 2.2, -2.98);
    luxuryBoardRoom.add(screenFrame);
    
    // Whiteboard
    const whiteboardMat = new THREE.MeshStandardMaterial({ 
      color: 0xFFFFFF,
      roughness: 0.3,
      metalness: 0.0,
    });
    const whiteboard = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 1, 0.05),
      whiteboardMat
    );
    whiteboard.position.set(2.5, 1.5, -2.95);
    luxuryBoardRoom.add(whiteboard);
    
    const whiteboardFrame = new THREE.Mesh(
      new THREE.BoxGeometry(1.6, 1.1, 0.08),
      luxuryFrameMat
    );
    whiteboardFrame.position.set(2.5, 1.5, -2.98);
    luxuryBoardRoom.add(whiteboardFrame);
    
    // Luxury chandelier/ceiling light
    const chandelierGroup = new THREE.Group();
    const lightMat = new THREE.MeshStandardMaterial({ 
      color: 0xD4AF37,
      roughness: 0.2,
      metalness: 0.9,
      emissive: 0xFFF8DC,
      emissiveIntensity: 0.3,
    });
    
    const chandelierBase = new THREE.Mesh(
      new THREE.CylinderGeometry(0.15, 0.2, 0.1, 16),
      lightMat
    );
    chandelierBase.position.y = 2.95;
    chandelierGroup.add(chandelierBase);
    
    
    
    // "BOARD ROOM" sign
    const signMat = new THREE.MeshStandardMaterial({ 
      color: 0xD4AF37,
      roughness: 0.3,
      metalness: 0.8,
      emissive: 0xFFD700,
      emissiveIntensity: 0.2,
    });
    const sign = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.2, 0.05),
      signMat
    );
    sign.position.set(0, 2.9, 2.6);
    luxuryBoardRoom.add(sign);
    
    luxuryBoardRoom.position.set(14, 0, -15);
    scene.add(luxuryBoardRoom);
    
    // Office plant
    const plantGroup = new THREE.Group();
    
    const potGeometry = new THREE.CylinderGeometry(0.35, 0.28, 0.5, 32);
    const potMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xB87C5C,
      roughness: 0.9,
    });
    const pot = new THREE.Mesh(potGeometry, potMaterial);
    pot.position.y = 0.25;
    pot.castShadow = true;
    plantGroup.add(pot);
    
    const potRim = new THREE.Mesh(
      new THREE.TorusGeometry(0.36, 0.03, 16, 32),
      potMaterial
    );
    potRim.rotation.x = Math.PI / 2;
    potRim.position.y = 0.5;
    plantGroup.add(potRim);
    
    const soil = new THREE.Mesh(
      new THREE.CylinderGeometry(0.33, 0.33, 0.08, 32),
      new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 1.0 })
    );
    soil.position.y = 0.48;
    plantGroup.add(soil);
    
    const leafMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x2d5016,
      roughness: 0.8,
      side: THREE.DoubleSide,
    });
    
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const height = 0.6 + Math.random() * 0.5;
      const radius = 0.2 + Math.random() * 0.15;
      
      const leaf = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, 8, 8),
        leafMaterial
      );
      leaf.scale.set(0.8, 1.5, 0.4);
      leaf.position.set(
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      );
      leaf.rotation.set(
        Math.random() * 0.5,
        angle,
        Math.random() * 0.3
      );
      leaf.castShadow = true;
      plantGroup.add(leaf);
    }
    
    plantGroup.position.set(-9, 0, 8);
    scene.add(plantGroup);
    
    // ==================== UNCLEPOOCHER CHARACTER ====================
    
    // Cache geometries and materials for optimization
    // Geometry and material caching removed - not needed for this implementation
    
    function createUnclepoocher() {
      const character = new THREE.Group();
      const parts = {};
      
      // === HEAD GROUP ===
      const headGroup = new THREE.Group();
      parts.head = headGroup;
      parts.headGroup = headGroup; // Store for animation
      
      // Enhanced dog fur materials with better texture properties
      const darkFurMat = new THREE.MeshStandardMaterial({ 
        color: 0x6b6b6b, // Dark gray fur
        roughness: 0.95,
        metalness: 0.0,
        bumpScale: 0.15, // More pronounced fur texture
        emissive: 0x000000,
        emissiveIntensity: 0.0,
      });
      
      const lightFurMat = new THREE.MeshStandardMaterial({ 
        color: 0xf5f5f5, // White chest/muzzle
        roughness: 0.9,
        metalness: 0.0,
        bumpScale: 0.12, // Subtle fur texture
        emissive: 0x000000,
        emissiveIntensity: 0.0,
      });
      
      // Main head - optimized geometry (reduced segments for performance)
      const headGeo = new THREE.SphereGeometry(0.38, 20, 20);
      const head = new THREE.Mesh(headGeo, darkFurMat);
      head.scale.set(1, 0.95, 1.1);
      head.castShadow = true;
      headGroup.add(head);
      
      // Add eyes with more realistic detail
      // Eye whites (sclera)
      const scleraMat = new THREE.MeshStandardMaterial({ 
        color: 0xf5f5f5,
        roughness: 0.6,
        metalness: 0.0,
      });
      const scleraGeo = new THREE.SphereGeometry(0.065, 14, 14);
      
      const leftEyeWhite = new THREE.Mesh(scleraGeo, scleraMat);
      leftEyeWhite.position.set(-0.12, 0.08, 0.42);
      leftEyeWhite.castShadow = true;
      headGroup.add(leftEyeWhite);
      
      const rightEyeWhite = new THREE.Mesh(scleraGeo, scleraMat);
      rightEyeWhite.position.set(0.12, 0.08, 0.42);
      rightEyeWhite.castShadow = true;
      headGroup.add(rightEyeWhite);
      
      // Iris (colored part)
      const irisMat = new THREE.MeshStandardMaterial({ 
        color: 0x4a6b8a, // Blue-gray eyes
        roughness: 0.3,
        metalness: 0.1,
      });
      const irisGeo = new THREE.SphereGeometry(0.045, 12, 12);
      
      const leftIris = new THREE.Mesh(irisGeo, irisMat);
      leftIris.position.set(-0.12, 0.08, 0.44);
      headGroup.add(leftIris);
      
      const rightIris = new THREE.Mesh(irisGeo, irisMat);
      rightIris.position.set(0.12, 0.08, 0.44);
      headGroup.add(rightIris);
      
      // Pupil
      const pupilMat = new THREE.MeshStandardMaterial({ 
        color: 0x0a0a0a,
        roughness: 0.1,
        metalness: 0.2,
        emissive: 0x050505,
        emissiveIntensity: 0.2,
      });
      const pupilGeo = new THREE.SphereGeometry(0.025, 10, 10);
      
      const leftPupil = new THREE.Mesh(pupilGeo, pupilMat);
      leftPupil.position.set(-0.12, 0.08, 0.46);
      headGroup.add(leftPupil);
      
      const rightPupil = new THREE.Mesh(pupilGeo, pupilMat);
      rightPupil.position.set(0.12, 0.08, 0.46);
      headGroup.add(rightPupil);
      
      // Eye highlights (more realistic)
      const highlightMat = new THREE.MeshStandardMaterial({ 
        color: 0xffffff,
        roughness: 0.0,
        metalness: 0.9,
        emissive: 0xffffff,
        emissiveIntensity: 0.3,
      });
      const highlightGeo = new THREE.SphereGeometry(0.018, 8, 8);
      const leftHighlight = new THREE.Mesh(highlightGeo, highlightMat);
      leftHighlight.position.set(-0.105, 0.095, 0.47);
      headGroup.add(leftHighlight);
      
      const rightHighlight = new THREE.Mesh(highlightGeo, highlightMat);
      rightHighlight.position.set(0.135, 0.095, 0.47);
      headGroup.add(rightHighlight);
      
      // Smaller secondary highlight
      const smallHighlightGeo = new THREE.SphereGeometry(0.008, 6, 6);
      const leftSmallHighlight = new THREE.Mesh(smallHighlightGeo, highlightMat);
      leftSmallHighlight.position.set(-0.13, 0.07, 0.47);
      headGroup.add(leftSmallHighlight);
      
      const rightSmallHighlight = new THREE.Mesh(smallHighlightGeo, highlightMat);
      rightSmallHighlight.position.set(0.11, 0.07, 0.47);
      headGroup.add(rightSmallHighlight);
      
      // Eyelids for more realism
      const eyelidMat = new THREE.MeshStandardMaterial({ 
        color: 0x5a5a5a,
        roughness: 0.85,
        metalness: 0.0,
      });
      const upperEyelidGeo = new THREE.SphereGeometry(0.07, 12, 12);
      upperEyelidGeo.scale(1, 0.3, 1);
      
      const leftUpperEyelid = new THREE.Mesh(upperEyelidGeo, eyelidMat);
      leftUpperEyelid.position.set(-0.12, 0.12, 0.41);
      leftUpperEyelid.rotation.x = -0.3;
      headGroup.add(leftUpperEyelid);
      
      const rightUpperEyelid = new THREE.Mesh(upperEyelidGeo, eyelidMat);
      rightUpperEyelid.position.set(0.12, 0.12, 0.41);
      rightUpperEyelid.rotation.x = -0.3;
      headGroup.add(rightUpperEyelid);
      
      // Eyebrows for expression
      const eyebrowMat = new THREE.MeshStandardMaterial({ 
        color: 0x4a4a4a,
        roughness: 0.9,
        metalness: 0.0,
      });
      const leftEyebrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.015, 0.01),
        eyebrowMat
      );
      leftEyebrow.position.set(-0.12, 0.15, 0.38);
      leftEyebrow.rotation.z = 0.2;
      headGroup.add(leftEyebrow);
      
      const rightEyebrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.015, 0.01),
        eyebrowMat
      );
      rightEyebrow.position.set(0.12, 0.15, 0.38);
      rightEyebrow.rotation.z = -0.2;
      headGroup.add(rightEyebrow);
      
      // White chest/muzzle area (optimized segments)
      const muzzleBase = new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 16, 16),
        lightFurMat
      );
      muzzleBase.position.set(0, -0.15, 0.25);
      muzzleBase.scale.set(1, 1.1, 1.2);
      muzzleBase.castShadow = true;
      headGroup.add(muzzleBase);
      
      // Snout - shorter and wider like spaniel (optimized)
      const snoutGeo = new THREE.SphereGeometry(0.22, 14, 14);
      const snout = new THREE.Mesh(snoutGeo, lightFurMat);
      snout.position.set(0, -0.2, 0.4);
      snout.scale.set(1.1, 0.8, 0.9);
      snout.castShadow = true;
      headGroup.add(snout);
      
      // Mouth line for detail
      const mouthLine = new THREE.Mesh(
        new THREE.CylinderGeometry(0.003, 0.003, 0.15, 8),
        new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.9 })
      );
      mouthLine.rotation.z = Math.PI / 2;
      mouthLine.position.set(0, -0.22, 0.52);
      headGroup.add(mouthLine);
      
      // Large black nose with better texture - more realistic
      const noseGeo = new THREE.SphereGeometry(0.11, 16, 16);
      const noseMat = new THREE.MeshStandardMaterial({ 
        color: 0x1a1a1a,
        roughness: 0.85, // More matte, leather-like
        metalness: 0.05,
        bumpScale: 0.1, // More texture for leathery feel
      });
      const nose = new THREE.Mesh(noseGeo, noseMat);
      nose.position.set(0, -0.18, 0.62);
      nose.scale.set(1.3, 1, 1.2);
      nose.castShadow = true;
      headGroup.add(nose);
      
      // Nostrils - more detailed
      const nostrilGeo = new THREE.SphereGeometry(0.018, 10, 10);
      const nostrilMat = new THREE.MeshStandardMaterial({ 
        color: 0x000000,
        roughness: 0.9,
        metalness: 0.0,
      });
      const leftNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
      leftNostril.position.set(-0.035, -0.18, 0.69);
      leftNostril.scale.set(1, 0.8, 1.2);
      headGroup.add(leftNostril);
      
      const rightNostril = new THREE.Mesh(nostrilGeo, nostrilMat);
      rightNostril.position.set(0.035, -0.18, 0.69);
      rightNostril.scale.set(1, 0.8, 1.2);
      headGroup.add(rightNostril);
      
      // Nose texture lines
      const noseLineMat = new THREE.MeshStandardMaterial({ 
        color: 0x0a0a0a,
        roughness: 0.9,
      });
      for (let i = -1; i <= 1; i += 2) {
        const noseLine = new THREE.Mesh(
          new THREE.BoxGeometry(0.002, 0.03, 0.01),
          noseLineMat
        );
        noseLine.position.set(i * 0.02, -0.2, 0.66);
        headGroup.add(noseLine);
      }
      
      // Long floppy ears - key feature! (optimized and enhanced)
      const earGeo = new THREE.SphereGeometry(0.25, 14, 14);
      
      const leftEar = new THREE.Mesh(earGeo, darkFurMat);
      leftEar.position.set(-0.32, 0.1, 0.05);
      leftEar.rotation.set(0.4, 0, 0.7);
      leftEar.scale.set(0.7, 1.8, 0.45);
      leftEar.castShadow = true;
      headGroup.add(leftEar);
      parts.leftEar = leftEar; // Store for animation
      
      const rightEar = new THREE.Mesh(earGeo, darkFurMat);
      rightEar.position.set(0.32, 0.1, 0.05);
      rightEar.rotation.set(0.4, 0, -0.7);
      rightEar.scale.set(0.7, 1.8, 0.45);
      rightEar.castShadow = true;
      headGroup.add(rightEar);
      parts.rightEar = rightEar; // Store for animation
      
      // Sunglasses! (key detail from reference)
      const glassesMat = new THREE.MeshStandardMaterial({ 
        color: 0x0a0a0a,
        roughness: 0.2,
        metalness: 0.8,
      });
      
      // Left lens
      const leftLens = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.12, 0.02),
        glassesMat
      );
      leftLens.position.set(-0.15, 0.08, 0.36);
      headGroup.add(leftLens);
      
      // Right lens
      const rightLens = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.12, 0.02),
        glassesMat
      );
      rightLens.position.set(0.15, 0.08, 0.36);
      headGroup.add(rightLens);
      
      // Bridge
      const bridge = new THREE.Mesh(
        new THREE.CylinderGeometry(0.015, 0.015, 0.12, 8),
        glassesMat
      );
      bridge.rotation.z = Math.PI / 2;
      bridge.position.set(0, 0.08, 0.37);
      headGroup.add(bridge);
      
      // Temples (arms of glasses)
      const templeGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.25, 8);
      
      const leftTemple = new THREE.Mesh(templeGeo, glassesMat);
      leftTemple.rotation.set(0.2, 0.5, 0);
      leftTemple.position.set(-0.25, 0.08, 0.25);
      headGroup.add(leftTemple);
      
      const rightTemple = new THREE.Mesh(templeGeo, glassesMat);
      rightTemple.rotation.set(0.2, -0.5, 0);
      rightTemple.position.set(0.25, 0.08, 0.25);
      headGroup.add(rightTemple);
      
      // Jowls/cheeks (spaniel feature)
      [-0.22, 0.22].forEach(xPos => {
        const jowl = new THREE.Mesh(
          new THREE.SphereGeometry(0.12, 12, 12),
          darkFurMat
        );
        jowl.position.set(xPos, -0.25, 0.35);
        jowl.scale.set(1, 1.3, 0.8);
        jowl.castShadow = true;
        headGroup.add(jowl);
      });
      
      headGroup.position.y = 1.75;
      character.add(headGroup);
      
      // === NECK ===
      const neckGeo = new THREE.CylinderGeometry(0.16, 0.2, 0.22, 16);
      const neck = new THREE.Mesh(neckGeo, darkFurMat);
      neck.position.y = 1.55;
      neck.castShadow = true;
      character.add(neck);
      
      // White chest fur visible above collar
      const chestFur = new THREE.Mesh(
        new THREE.SphereGeometry(0.18, 16, 16),
        lightFurMat
      );
      chestFur.position.set(0, 1.48, 0.12);
      chestFur.scale.set(1, 0.8, 0.6);
      character.add(chestFur);
      
      // === TORSO - BROWN SUIT ===
      const torsoGroup = new THREE.Group();
      parts.torso = torsoGroup;
      
      // Enhanced brown suit material with fabric texture
      const brownSuitMat = new THREE.MeshStandardMaterial({ 
        color: 0x6b4423, // Brown suit
        roughness: 0.82,
        metalness: 0.02,
        bumpScale: 0.12, // Fabric texture simulation
      });
      
      // Optimized torso geometries (reduced segments)
      const upperTorso = new THREE.Mesh(
        new THREE.CylinderGeometry(0.32, 0.36, 0.6, 20),
        brownSuitMat
      );
      upperTorso.position.y = 1.2;
      upperTorso.castShadow = true;
      torsoGroup.add(upperTorso);
      
      const lowerTorso = new THREE.Mesh(
        new THREE.CylinderGeometry(0.36, 0.34, 0.4, 20),
        brownSuitMat
      );
      lowerTorso.position.y = 0.75;
      lowerTorso.castShadow = true;
      torsoGroup.add(lowerTorso);
      
      // Add suit creases/wrinkles for realism
      const creaseMat = new THREE.MeshStandardMaterial({ 
        color: 0x5a3413,
        roughness: 0.9,
        transparent: true,
        opacity: 0.3,
      });
      
      // Shoulder crease
      const shoulderCrease = new THREE.Mesh(
        new THREE.BoxGeometry(0.15, 0.02, 0.25),
        creaseMat
      );
      shoulderCrease.rotation.z = 0.1;
      shoulderCrease.position.set(-0.25, 1.35, 0.25);
      torsoGroup.add(shoulderCrease);
      
      // Enhanced yellow shirt material
      const yellowShirtMat = new THREE.MeshStandardMaterial({ 
        color: 0xf5e6a0, // Pale yellow
        roughness: 0.75,
        metalness: 0.0,
        bumpScale: 0.08, // Fabric texture
      });
      // Optimized shirt geometry
      const shirt = new THREE.Mesh(
        new THREE.CylinderGeometry(0.27, 0.31, 0.85, 20),
        yellowShirtMat
      );
      shirt.position.y = 1.02;
      torsoGroup.add(shirt);
      
      // Shirt collar
      const collarGeo = new THREE.BoxGeometry(0.35, 0.12, 0.02);
      
      const leftCollar = new THREE.Mesh(collarGeo, yellowShirtMat);
      leftCollar.position.set(-0.12, 1.48, 0.28);
      leftCollar.rotation.set(0, -0.3, -0.2);
      torsoGroup.add(leftCollar);
      
      const rightCollar = new THREE.Mesh(collarGeo, yellowShirtMat);
      rightCollar.position.set(0.12, 1.48, 0.28);
      rightCollar.rotation.set(0, 0.3, 0.2);
      torsoGroup.add(rightCollar);
      
      // Striped tie (yellow and dark stripes like reference)
      const tieMat = new THREE.MeshStandardMaterial({ 
        color: 0xe6b800, // Gold/yellow
        roughness: 0.6,
        metalness: 0.2,
      });
      
      const tie = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055, 0.07, 0.7, 8),
        tieMat
      );
      tie.position.set(0, 1.0, 0.31);
      torsoGroup.add(tie);
      
      // Tie knot
      const tieKnot = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.08, 0.06),
        tieMat
      );
      tieKnot.position.set(0, 1.42, 0.31);
      torsoGroup.add(tieKnot);
      
      // Suit lapels
      const lapelGeo = new THREE.BoxGeometry(0.15, 0.5, 0.03);
      
      const leftLapel = new THREE.Mesh(lapelGeo, brownSuitMat);
      leftLapel.position.set(-0.2, 1.3, 0.32);
      leftLapel.rotation.set(0, -0.2, 0.15);
      leftLapel.castShadow = true;
      torsoGroup.add(leftLapel);
      
      const rightLapel = new THREE.Mesh(lapelGeo, brownSuitMat);
      rightLapel.position.set(0.2, 1.3, 0.32);
      rightLapel.rotation.set(0, 0.2, -0.15);
      rightLapel.castShadow = true;
      torsoGroup.add(rightLapel);
      
      // Suit buttons
      const buttonGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.02, 12);
      const buttonMat = new THREE.MeshStandardMaterial({ 
        color: 0x3a2a1a,
        roughness: 0.4,
        metalness: 0.6,
      });
      
      [1.3, 1.05, 0.8].forEach(y => {
        const button = new THREE.Mesh(buttonGeo, buttonMat);
        button.rotation.x = Math.PI / 2;
        button.position.set(0.22, y, 0.35);
        torsoGroup.add(button);
      });
      
      // Suit pockets
      const pocketGeo = new THREE.BoxGeometry(0.12, 0.15, 0.02);
      
      [-0.22, 0.22].forEach(x => {
        const pocket = new THREE.Mesh(pocketGeo, brownSuitMat);
        pocket.position.set(x, 0.85, 0.35);
        torsoGroup.add(pocket);
        
        // Pocket flap
        const flap = new THREE.Mesh(
          new THREE.BoxGeometry(0.12, 0.04, 0.025),
          brownSuitMat
        );
        flap.position.set(x, 0.93, 0.352);
        torsoGroup.add(flap);
      });
      
      character.add(torsoGroup);
      
      // === ARMS ===
      const createArm = (side) => {
        const armGroup = new THREE.Group();
        const x = side === 'left' ? -0.42 : 0.42;
        armGroup.position.set(x, 1.3, 0);
        
        // Optimized arm geometries (reduced segments)
        const upperArm = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.09, 0.4, 10, 10),
          brownSuitMat
        );
        upperArm.position.y = -0.25;
        upperArm.castShadow = true;
        armGroup.add(upperArm);
        
        const forearm = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.08, 0.35, 10, 10),
          brownSuitMat
        );
        forearm.position.y = -0.62;
        forearm.castShadow = true;
        armGroup.add(forearm);
        
        // Enhanced paw/hand with fur (optimized)
        const hand = new THREE.Mesh(
          new THREE.SphereGeometry(0.09, 12, 12),
          darkFurMat
        );
        hand.position.y = -0.86;
        hand.scale.set(1.1, 1.3, 0.9);
        hand.castShadow = true;
        armGroup.add(hand);
        
        // Add paw pads for detail - more realistic
        const padMat = new THREE.MeshStandardMaterial({ 
          color: 0x3a3a3a,
          roughness: 0.8,
          metalness: 0.0,
        });
        const padGeo = new THREE.SphereGeometry(0.022, 10, 10);
        // Main paw pad
        const mainPad = new THREE.Mesh(padGeo, padMat);
        mainPad.position.set(0, -0.88, 0.02);
        mainPad.scale.set(1.2, 0.6, 1);
        armGroup.add(mainPad);
        
        // Individual toe pads
        [-0.04, 0, 0.04].forEach(xOffset => {
          const toePad = new THREE.Mesh(
            new THREE.SphereGeometry(0.015, 8, 8),
            padMat
          );
          toePad.position.set(xOffset, -0.89, 0.025);
          toePad.scale.set(1, 0.7, 1);
          armGroup.add(toePad);
        });
        
        // Claws
        const clawMat = new THREE.MeshStandardMaterial({ 
          color: 0x2a2a2a,
          roughness: 0.4,
          metalness: 0.3,
        });
        [-0.04, 0, 0.04].forEach(xOffset => {
          const claw = new THREE.Mesh(
            new THREE.ConeGeometry(0.008, 0.015, 6),
            clawMat
          );
          claw.rotation.x = Math.PI;
          claw.position.set(xOffset, -0.91, 0.03);
          armGroup.add(claw);
        });
        
        return armGroup;
      };
      
      const leftArm = createArm('left');
      parts.leftArm = leftArm;
      character.add(leftArm);
      
      const rightArm = createArm('right');
      parts.rightArm = rightArm;
      character.add(rightArm);
      
      // === LEGS ===
      // Enhanced pants material
      const pantsMat = new THREE.MeshStandardMaterial({ 
        color: 0x5a4a3a, // Dark brown pants
        roughness: 0.88,
        metalness: 0.0,
        bumpScale: 0.1, // Fabric texture
      });
      
      // Enhanced shoe material
      const shoeMat = new THREE.MeshStandardMaterial({ 
        color: 0x2a1a0a,
        roughness: 0.35,
        metalness: 0.4,
        bumpScale: 0.08, // Leather texture
      });
      
      const createLeg = (side) => {
        const legGroup = new THREE.Group();
        const x = side === 'left' ? -0.16 : 0.16;
        legGroup.position.set(x, 0.55, 0);
        
        // Optimized leg geometries
        const thigh = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.11, 0.45, 10, 10),
          pantsMat
        );
        thigh.position.y = -0.28;
        thigh.castShadow = true;
        legGroup.add(thigh);
        
        const shin = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.1, 0.4, 10, 10),
          pantsMat
        );
        shin.position.y = -0.68;
        shin.castShadow = true;
        legGroup.add(shin);
        
        // Enhanced shoe with better detail
        const shoe = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.08, 0.16, 8, 10),
          shoeMat
        );
        shoe.rotation.x = Math.PI / 2;
        shoe.position.set(0, -0.93, 0.05);
        shoe.scale.set(1.15, 1.3, 1);
        shoe.castShadow = true;
        legGroup.add(shoe);
        
        // Shoe sole
        const soleMat = new THREE.MeshStandardMaterial({ 
          color: 0x1a0a0a,
          roughness: 0.8,
        });
        const sole = new THREE.Mesh(
          new THREE.BoxGeometry(0.18, 0.02, 0.28),
          soleMat
        );
        sole.position.set(0, -0.98, 0.05);
        sole.castShadow = true;
        legGroup.add(sole);
        
        return legGroup;
      };
      
      const leftLeg = createLeg('left');
      parts.leftLeg = leftLeg;
      character.add(leftLeg);
      
      const rightLeg = createLeg('right');
      parts.rightLeg = rightLeg;
      character.add(rightLeg);
      
      // === TAIL ===
      const tailGroup = new THREE.Group();
      parts.tail = tailGroup;
      
      // Tail base
      const tailBase = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.08, 0.15, 10, 10),
        darkFurMat
      );
      tailBase.position.set(0, 0.5, -0.2);
      tailBase.rotation.x = Math.PI / 6;
      tailBase.castShadow = true;
      tailGroup.add(tailBase);
      parts.tailBase = tailBase;
      
      // Tail middle
      const tailMiddle = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.06, 0.2, 10, 10),
        darkFurMat
      );
      tailMiddle.position.set(0, 0.35, -0.35);
      tailMiddle.rotation.x = Math.PI / 4;
      tailMiddle.castShadow = true;
      tailGroup.add(tailMiddle);
      parts.tailMiddle = tailMiddle;
      
      // Tail tip
      const tailTip = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.04, 0.15, 8, 8),
        darkFurMat
      );
      tailTip.position.set(0, 0.2, -0.5);
      tailTip.rotation.x = Math.PI / 3;
      tailTip.castShadow = true;
      tailGroup.add(tailTip);
      parts.tailTip = tailTip;
      
      // White tip on tail
      const tailTipWhite = new THREE.Mesh(
        new THREE.SphereGeometry(0.045, 8, 8),
        lightFurMat
      );
      tailTipWhite.position.set(0, 0.15, -0.58);
      tailGroup.add(tailTipWhite);
      
      character.add(tailGroup);
      
      character.userData.parts = parts;
      return character;
    }
    
    const unclepoocher = createUnclepoocher();
    scene.add(unclepoocher);
    
    // ==================== PATROL ROUTE ====================
    
    const patrolRoute = [
      { x: -10, z: -10 },
      { x: -5, z: -10 },
      { x: 0, z: -10 },
      { x: 5, z: -10 },
      { x: 10, z: -10 },
      { x: 10, z: -5 },
      { x: 10, z: 0 },
      { x: 10, z: 5 },
      { x: 10, z: 10 },
      { x: 5, z: 10 },
      { x: 0, z: 10 },
      { x: -5, z: 10 },
      { x: -10, z: 10 },
      { x: -10, z: 5 },
      { x: -10, z: 0 },
      { x: -10, z: -5 },
      { x: -10, z: -10 }, // Close the loop - end at start
    ];
    
    unclepoocher.position.set(patrolRoute[0].x, 0.9, patrolRoute[0].z);
    
    // ==================== ANIMATION LOOP ====================
    
    let totalPathProgress = 0; // Continuous progress along entire path
    const totalPathLength = patrolRoute.length - 1; // -1 because last point equals first
    const globalSpeed = 0.005; // Global movement speed
    let animationTime = 0;
    
    // Cache frequently used values for optimization
    const parts = unclepoocher.userData.parts;
    const PI = Math.PI;
    const sin = Math.sin;
    const abs = Math.abs;
    const floorMath = Math.floor;
    const atan2 = Math.atan2;
    
    function animate() {
      requestAnimationFrame(animate);
      
      // Increment continuous progress
      totalPathProgress += globalSpeed;
      
      // Loop back smoothly (no modulo, no jumping)
      if (totalPathProgress >= totalPathLength) {
        totalPathProgress = totalPathProgress - totalPathLength;
      }
      
      // Find which segment we're on (cached calculation)
      const segmentIndex = floorMath(totalPathProgress);
      const segmentProgress = totalPathProgress - segmentIndex;
      
      // Get current and next points (with wrapping)
      const currentPoint = patrolRoute[segmentIndex];
      const nextPoint = patrolRoute[segmentIndex + 1];
      
      // Smooth easing within segment (cached)
      const t = segmentProgress < 0.5 
        ? 2 * segmentProgress * segmentProgress 
        : 1 - Math.pow(-2 * segmentProgress + 2, 2) / 2;
      
      // Interpolate position smoothly
      const dx = nextPoint.x - currentPoint.x;
      const dz = nextPoint.z - currentPoint.z;
      unclepoocher.position.x = currentPoint.x + dx * t;
      unclepoocher.position.z = currentPoint.z + dz * t;
      
      // Calculate direction for rotation (only if there's movement)
      if (abs(dx) > 0.01 || abs(dz) > 0.01) {
        const targetRotation = atan2(dx, dz);
        let currentRot = unclepoocher.rotation.y;
        
        // Calculate shortest rotation path
        let diff = targetRotation - currentRot;
        while (diff > PI) diff -= PI * 2;
        while (diff < -PI) diff += PI * 2;
        
        // Smooth rotation
        unclepoocher.rotation.y = currentRot + diff * 0.08;
      }
      
      // Walking animation - completely independent of position
      animationTime += 0.12;
      
      // Cache sin calculations
      const sinTime = sin(animationTime);
      const sinTime2 = sin(animationTime * 2);
      const sinTimePI = sin(animationTime + PI);
      
      // Body bob - more natural
      unclepoocher.position.y = 0.9 + abs(sinTime2) * 0.04;
      
      // Subtle breathing animation
      const breathing = sin(animationTime * 0.3) * 0.01;
      if (parts.torso) {
        parts.torso.scale.y = 1 + breathing;
        parts.torso.scale.x = 1 + breathing * 0.5;
      }
      
      // Head subtle movement (looking around)
      if (parts.headGroup) {
        parts.headGroup.rotation.y = sin(animationTime * 0.15) * 0.05;
        parts.headGroup.rotation.x = sin(animationTime * 0.2) * 0.02;
      }
      
      // Legs - more natural walking
      if (parts.leftLeg && parts.rightLeg) {
        parts.leftLeg.rotation.x = sinTime * 0.35;
        parts.leftLeg.rotation.z = sinTime * 0.05;
        parts.rightLeg.rotation.x = sinTimePI * 0.35;
        parts.rightLeg.rotation.z = sinTimePI * 0.05;
      }
      
      // Arms - more natural swinging
      if (parts.leftArm && parts.rightArm) {
        parts.leftArm.rotation.x = sinTimePI * 0.22;
        parts.leftArm.rotation.z = sinTimePI * 0.08;
        parts.rightArm.rotation.x = sinTime * 0.22;
        parts.rightArm.rotation.z = sinTime * 0.08;
      }
      
      // Torso - natural sway
      if (parts.torso) {
        parts.torso.rotation.z = sinTime * 0.015;
        parts.torso.rotation.y = sinTime * 0.01;
      }
      
      // Ear flop animation (more lifelike)
      if (parts.leftEar && parts.rightEar) {
        const earFlop = sin(animationTime * 0.5) * 0.1;
        const earSway = sin(animationTime * 0.3) * 0.05;
        parts.leftEar.rotation.z = 0.7 + earFlop;
        parts.leftEar.rotation.y = earSway;
        parts.rightEar.rotation.z = -0.7 - earFlop;
        parts.rightEar.rotation.y = -earSway;
      }
      
      // Tail wagging - very lifelike!
      if (parts.tail) {
        const wagSpeed = 1.2;
        const wagAmount = sin(animationTime * wagSpeed) * 0.4;
        const wagAmount2 = sin(animationTime * wagSpeed * 1.5) * 0.25;
        parts.tail.rotation.y = wagAmount;
        parts.tail.rotation.z = wagAmount2 * 0.3;
        
        // Tail segments follow with delay for natural movement
        if (parts.tailMiddle) {
          parts.tailMiddle.rotation.y = wagAmount * 0.7;
        }
        if (parts.tailTip) {
          parts.tailTip.rotation.y = wagAmount * 0.5;
        }
      }
      
      // Animate server lights blinking (optimized)
      const lightsLength = serverLights.length;
      for (let i = 0; i < lightsLength; i++) {
        const lightObj = serverLights[i];
        const blinkValue = sin(animationTime * lightObj.blinkSpeed + lightObj.phase);
        lightObj.mesh.material.emissiveIntensity = blinkValue > 0 ? 0.8 : 0.2;
      }
      
      renderer.render(scene, camera);
    }
    
    animate();
    
       // ==================== RESIZE HANDLER ====================
    
       function handleResize() {
        const aspect = window.innerWidth / window.innerHeight;
        const isMobile = window.innerWidth < 768;
        const frustumSize = isMobile ? 65 : 18; // Same as camera setup
        
        camera.left = -frustumSize * aspect;
        camera.right = frustumSize * aspect;
        camera.top = frustumSize;
        camera.bottom = -frustumSize;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }
      
      window.addEventListener('resize', handleResize);
      
      // ==================== TOUCH CONTROLS (Pinch to Zoom) - MOBILE ONLY ====================
      if (isMobile) {
        let lastTouchDistance = 0;
        let currentZoomLevel = frustumSize;
        const minZoom = 18; // Desktop resolution
        const maxZoom = 65; // Max zoom out
        
        function getTouchDistance(touches) {
          const dx = touches[0].clientX - touches[1].clientX;
          const dy = touches[0].clientY - touches[1].clientY;
          return Math.sqrt(dx * dx + dy * dy);
        }
        
        function handleTouchStart(e) {
          if (e.touches.length === 2) {
            lastTouchDistance = getTouchDistance(e.touches);
          }
        }
        
        function handleTouchMove(e) {
          if (e.touches.length === 2) {
            e.preventDefault();
            
            const currentDistance = getTouchDistance(e.touches);
            
            if (lastTouchDistance > 0) {
              const delta = currentDistance - lastTouchDistance;
              const zoomSpeed = 0.08; // Increased sensitivity
              
              // Pinch out (fingers apart) = zoom in (decrease frustum)
              // Pinch in (fingers together) = zoom out (increase frustum)
              currentZoomLevel -= delta * zoomSpeed;
              currentZoomLevel = Math.max(minZoom, Math.min(maxZoom, currentZoomLevel));
              
              const aspect = window.innerWidth / window.innerHeight;
              camera.left = -currentZoomLevel * aspect;
              camera.right = currentZoomLevel * aspect;
              camera.top = currentZoomLevel;
              camera.bottom = -currentZoomLevel;
              camera.updateProjectionMatrix();
            }
            
            lastTouchDistance = currentDistance;
          }
        }
        
        function handleTouchEnd(e) {
          if (e.touches.length < 2) {
            lastTouchDistance = 0;
          }
        }
        
        // Add touch event listeners to the canvas element
        const canvas = renderer.domElement;
        canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
        
        // Add cleanup for touch events
        const originalCleanup = () => {
          canvas.removeEventListener('touchstart', handleTouchStart);
          canvas.removeEventListener('touchmove', handleTouchMove);
          canvas.removeEventListener('touchend', handleTouchEnd);
        };
        
        // Store cleanup function
        window.__touchCleanup = originalCleanup;
      }
      
    // ==================== CLEANUP ====================
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentMount && renderer.domElement.parentNode === currentMount) {
        currentMount.removeChild(renderer.domElement);
      }
      
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(mat => mat.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      
      renderer.dispose();
    };
    
  }, []);
  
  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      
      <div style={{
        position: 'absolute',
        top: isMobile ? '38%' : '42%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff',
        border: '3px solid #FF6B35',
        borderRadius: '10px',
        padding: isMobile ? '10px 15px' : '20px 40px',
        boxShadow: '6px 6px 0px rgba(0,0,0,0.2)',
        fontFamily: '"Courier New", monospace',
        zIndex: 1000,
        maxWidth: isMobile ? '70%' : '80%',
        cursor: isMobile ? 'pointer' : 'default',
      }}
      onClick={() => isMobile && setShowModal(true)}
      >
        <h1 style={{
          margin: 0,
          fontSize: isMobile ? '20px' : '36px',
          fontWeight: 'bold',
          color: '#2C2C2C',
          letterSpacing: isMobile ? '0.5px' : '1.5px',
          textAlign: 'center',
        }}>
          COMING SOON
        </h1>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: isMobile ? '9px' : '14px',
          color: '#666',
          textAlign: 'center',
        }}>
          Unclepoocher is on patrol
        </p>
        {!isMobile && (
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '2px solid #FF6B35',
            fontSize: '12px',
            color: '#555',
            textAlign: 'center',
          }}>
            <p style={{ margin: '4px 0', fontWeight: 'bold' }}>🚀 Built in Public</p>
            <p style={{ margin: '4px 0' }}></p>
          </div>
        )}
        {isMobile && (
          <p style={{
            margin: '6px 0 0 0',
            fontSize: '8px',
            color: '#999',
            textAlign: 'center',
          }}>
            Tap for details
          </p>
        )}
      </div>
      
      {/* Modal for mobile */}
      {showModal && isMobile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div style={{
            backgroundColor: '#fff',
            border: '3px solid #FF6B35',
            borderRadius: '10px',
            padding: '25px 30px',
            boxShadow: '6px 6px 0px rgba(0,0,0,0.3)',
            fontFamily: '"Courier New", monospace',
            maxWidth: '85%',
            maxHeight: '80%',
            overflow: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h1 style={{
              margin: 0,
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#2C2C2C',
              letterSpacing: '1.5px',
              textAlign: 'center',
            }}>
              COMING SOON
            </h1>
            <p style={{
              margin: '12px 0',
              fontSize: '16px',
              color: '#666',
              textAlign: 'center',
            }}>
              Unclepoocher is on patrol
            </p>
            <div style={{
              marginTop: '15px',
              paddingTop: '15px',
              borderTop: '2px solid #FF6B35',
              fontSize: '14px',
              color: '#555',
              textAlign: 'center',
            }}>
              <p style={{ margin: '8px 0', fontWeight: 'bold' }}>🚀 Built in Public</p>
              <p style={{ margin: '8px 0', fontSize: '12px' }}>
                A 3D interactive experience showcasing React & Three.js
              </p>
            </div>
            <button
              onClick={() => setShowModal(false)}
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '12px',
                backgroundColor: '#FF6B35',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: '"Courier New", monospace',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      <div style={{
        position: 'absolute',
        bottom: isMobile ? '60px' : '30px', // Moved up significantly on mobile
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        color: '#2C2C2C',
        fontSize: isMobile ? '9px' : '14px',
        fontFamily: '"Courier New", monospace',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: isMobile ? '8px 12px' : '15px 25px',
        borderRadius: '8px',
        border: '2px solid #FF6B35',
        maxWidth: isMobile ? '85%' : '90%',
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: isMobile ? '9px' : '14px' }}>
          Something amazing is being built...
        </p>
        <p style={{ margin: '0', fontSize: isMobile ? '8px' : '12px', color: '#666' }}>
          Built with | React • Three.js • WebGL • AI-Assisted Development
        </p>
      </div>
    </div>
  );
}
